import { createClient } from '@supabase/supabase-js'

const SUPABASE_URL = 'https://mhqmasmmstwjxmjhbaza.supabase.co'
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1ocW1hc21tc3R3anhtamhiYXphIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI2NDM0OTUsImV4cCI6MjA4ODIxOTQ5NX0.UQaC2Di6iOxg_x4BeuDUPnWA9HCuNG_GLSj0XSLItc0'

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase variables')
}

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: { persistSession: true, autoRefreshToken: true },
  global: { headers: { 'X-Client-Info': 'sidehustlr-builder' } },
  db: { schema: 'public' },
  realtime: { params: { eventsPerSecond: 10 } },
})

// ─────────────────────────────────────────────────────────────────────────────
// In-Memory Query Cache (SWR-style)
// ─────────────────────────────────────────────────────────────────────────────
const queryCache = new Map()
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

export function getCached(key) {
  const entry = queryCache.get(key)
  if (!entry) return null
  if (Date.now() - entry.timestamp > CACHE_TTL) { queryCache.delete(key); return null }
  return entry.data
}

export function setCached(key, data) {
  queryCache.set(key, { data, timestamp: Date.now() })
}

export function invalidateCache(keyPrefix) {
  for (const key of queryCache.keys()) {
    if (key.startsWith(keyPrefix)) queryCache.delete(key)
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// localStorage helpers (persistent cache across sessions)
// ─────────────────────────────────────────────────────────────────────────────
const LS_TTL = 30 * 60 * 1000 // 30 minutes

export function lsGet(key) {
  try {
    const raw = localStorage.getItem(`sh_cache_${key}`)
    if (!raw) return null
    const { data, ts } = JSON.parse(raw)
    if (Date.now() - ts > LS_TTL) { localStorage.removeItem(`sh_cache_${key}`); return null }
    return data
  } catch { return null }
}

export function lsSet(key, data) {
  try { localStorage.setItem(`sh_cache_${key}`, JSON.stringify({ data, ts: Date.now() })) } catch {}
}

export function lsClear(keyPrefix) {
  try {
    Object.keys(localStorage)
      .filter(k => k.startsWith(`sh_cache_${keyPrefix}`))
      .forEach(k => localStorage.removeItem(k))
  } catch {}
}

// ─────────────────────────────────────────────────────────────────────────────
// Connection Health & Auto-Reconnect
// ─────────────────────────────────────────────────────────────────────────────
let healthCheckInterval = null
let lastHealthCheck = Date.now()
let isHealthy = true
let reconnectAttempts = 0
const MAX_RECONNECT_ATTEMPTS = 5
const HEALTH_CHECK_INTERVAL = 4 * 60 * 1000 // 4 minutes
const RECONNECT_DELAY = 2000

export async function checkConnectionHealth() {
  try {
    // Add timeout to prevent hanging health checks
    const { error } = await Promise.race([
      supabase.from('websites_1772750000000').select('id').limit(1).maybeSingle(),
      new Promise((_, reject) => setTimeout(() => reject(new Error('Health check timeout')), 5000))
    ])
    if (error && error.code !== 'PGRST116') {
      isHealthy = false; return false
    }
    lastHealthCheck = Date.now(); isHealthy = true; reconnectAttempts = 0
    return true
  } catch {
    isHealthy = false; return false
  }
}

export async function attemptReconnect() {
  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) return false
  reconnectAttempts++
  try {
    // Add timeout to prevent hanging refresh attempts
    const result = await Promise.race([
      supabase.auth.refreshSession(),
      new Promise((_, reject) => setTimeout(() => reject(new Error('Refresh session timeout')), 5000))
    ])
    const { data, error } = result || {}
    if (!error && data?.session) { isHealthy = true; reconnectAttempts = 0; return true }
    await new Promise(r => setTimeout(r, RECONNECT_DELAY * reconnectAttempts))
    return await checkConnectionHealth()
  } catch {
    await new Promise(r => setTimeout(r, RECONNECT_DELAY * reconnectAttempts))
    return false
  }
}

export function startHealthMonitoring() {
  if (healthCheckInterval) clearInterval(healthCheckInterval)
  checkConnectionHealth()
  healthCheckInterval = setInterval(async () => {
    const healthy = await checkConnectionHealth()
    if (!healthy) await attemptReconnect()
  }, HEALTH_CHECK_INTERVAL)
}

export function stopHealthMonitoring() {
  if (healthCheckInterval) { clearInterval(healthCheckInterval); healthCheckInterval = null }
}

export function getConnectionStatus() {
  return { isHealthy, lastHealthCheck, reconnectAttempts, timeSinceLastCheck: Date.now() - lastHealthCheck }
}

// ─────────────────────────────────────────────────────────────────────────────
// withRetry — wraps any Supabase call with exponential backoff and timeout
// ─────────────────────────────────────────────────────────────────────────────
export async function withRetry(queryFn, maxRetries = 3, retryDelay = 1000, timeoutMs = 8000) {
  let lastError = null
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      if (!isHealthy && attempt > 0) await attemptReconnect()
      
      // Wrap the query in a Promise.race to enforce a strict timeout
      const result = await Promise.race([
        queryFn(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Query timeout')), timeoutMs))
      ])
      
      if (result && !result.error) return result
      
      lastError = result?.error || new Error('Unknown error')
      
      const isRetryable =
        lastError.message?.includes('timeout') ||
        lastError.message?.includes('network') ||
        lastError.message?.includes('fetch') ||
        lastError.code === 'PGRST301' ||
        lastError.code === '57P01' ||
        lastError.code === '08006'
        
      if (!isRetryable || attempt === maxRetries) return result
      
      console.warn(`Retry ${attempt + 1}/${maxRetries}:`, lastError.message)
      await new Promise(r => setTimeout(r, retryDelay * Math.pow(2, attempt)))
    } catch (err) {
      lastError = err
      if (attempt === maxRetries) throw err
      await new Promise(r => setTimeout(r, retryDelay * Math.pow(2, attempt)))
    }
  }
  throw lastError || new Error('Query failed after maximum retries')
}

// ─────────────────────────────────────────────────────────────────────────────
// fetchWithCache — SWR pattern: return cache instantly, refresh in background
// Usage: fetchWithCache('key', () => supabase.from(...).select(...))
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchWithCache(cacheKey, queryFn, { onUpdate, persistent = false } = {}) {
  // 1. Return cached data immediately
  const memCached = getCached(cacheKey)
  if (memCached) {
    if (onUpdate) onUpdate(memCached, false)
    // Background revalidate
    ;(async () => {
      try {
        const result = await withRetry(queryFn, 2, 800)
        if (!result.error && result.data) {
          setCached(cacheKey, result.data)
          if (persistent) lsSet(cacheKey, result.data)
          if (onUpdate) onUpdate(result.data, true)
        }
      } catch {}
    })()
    return memCached
  }

  // 2. Try persistent localStorage cache
  if (persistent) {
    const lsCached = lsGet(cacheKey)
    if (lsCached) {
      setCached(cacheKey, lsCached)
      if (onUpdate) onUpdate(lsCached, false)
      // Background revalidate
      ;(async () => {
        try {
          const result = await withRetry(queryFn, 2, 800)
          if (!result.error && result.data) {
            setCached(cacheKey, result.data)
            lsSet(cacheKey, result.data)
            if (onUpdate) onUpdate(result.data, true)
          }
        } catch {}
      })()
      return lsCached
    }
  }

  // 3. No cache — fetch fresh
  const result = await withRetry(queryFn, 3, 1000)
  if (!result.error && result.data) {
    setCached(cacheKey, result.data)
    if (persistent) lsSet(cacheKey, result.data)
  }
  if (onUpdate && result.data) onUpdate(result.data, true)
  return result.data || null
}

// ─────────────────────────────────────────────────────────────────────────────
// Realtime subscription helper
// ─────────────────────────────────────────────────────────────────────────────
export function subscribeToTable(table, filter, callback) {
  const channel = supabase
    .channel(`realtime-${table}-${JSON.stringify(filter)}`)
    .on('postgres_changes', { event: '*', schema: 'public', table, ...filter }, payload => {
      callback(payload)
    })
    .subscribe()
  return () => supabase.removeChannel(channel)
}

// ─────────────────────────────────────────────────────────────────────────────
// Parallel fetch helper
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchParallel(queries) {
  return Promise.allSettled(queries.map(q => withRetry(q, 2, 800)))
}

// ─────────────────────────────────────────────────────────────────────────────
// Auto-start health monitoring
// ─────────────────────────────────────────────────────────────────────────────
if (typeof window !== 'undefined') {
  startHealthMonitoring()
  window.addEventListener('beforeunload', stopHealthMonitoring)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) checkConnectionHealth()
  })
  window.addEventListener('online', async () => {
    const healthy = await checkConnectionHealth()
    if (!healthy) await attemptReconnect()
  })
}

export default supabase